import { getSupportedCountries, findCountryByKey } from './country-config'

const getCountryCodeFromIso2 = (isoCode2) => {
  const foundCountry = findCountryByKey('isoCode2', isoCode2)

  if (!foundCountry) {
    const supportedCountries = getSupportedCountries()

    return supportedCountries[0]?.countryCode
  }

  return foundCountry.countryCode
}

const getIos2FromCountryCode = (countryCode) => {
  const foundCountry = findCountryByKey('countryCode', countryCode)

  if (!foundCountry) {
    const supportedCountries = getSupportedCountries()

    return supportedCountries[0]?.isoCode2
  }

  return foundCountry.isoCode2
}

const getPhoneNumberConfigBeginWith = (
  phoneNumber,
  listPhoneNumberConfigBeginWith
) => {
  if (!TypeChecker.isArray(listPhoneNumberConfigBeginWith)) {
    return null
  }

  return listPhoneNumberConfigBeginWith.find((config) =>
    config?.pattern?.test(phoneNumber)
  )
}

const getPhoneNumberByIsoCode2 = (phoneNumberWithCountryCode, isoCode2) => {
  if (!phoneNumberWithCountryCode) {
    return {
      countryCode: '',
      phoneNumber: '',
    }
  }

  const countryCode = getCountryCodeFromIso2(isoCode2)
  const phoneNumber = phoneNumberWithCountryCode.replace(countryCode, '')

  return { countryCode, phoneNumber }
}

const getPhoneNumberWithSpaces = (number) => {
  const digits = `${number}`.replace(/\D/g, '')
  if (digits.length <= 3) return digits
  const part1 = digits.slice(0, 3) // first 3 digits
  const part2 = digits.slice(3, 5) // next 2 digits
  const part3 = digits.slice(5) // rest
  return [part1, part2, part3].filter(Boolean).join(' ')
}

/**
 * Build { countryCode, phoneNumber } for auth store / formatters.
 * Session order uses split fields; profile may return a single `phoneNumber` string
 * that already includes the country calling code.
 *
 * @param {Record<string, unknown>} [customer]
 * @returns {{ countryCode: string, phoneNumber: string }}
 */
const parseCustomerPhoneForAuth = (customer = {}) => {
  const raw = customer.phoneNumber

  if (TypeChecker.isObject(raw) && raw !== null && 'phoneNumber' in raw) {
    return {
      countryCode: `${raw.countryCode ?? ''}`.replace(/\D/g, ''),
      phoneNumber: `${raw.phoneNumber ?? ''}`.replace(/\D/g, ''),
    }
  }

  const digits = `${raw ?? ''}`.replace(/\D/g, '')

  const resolveDialFromCustomerCountry = () => {
    const cc = customer.countryCode
    if (cc === undefined || cc === null || cc === '') {
      return ''
    }
    const ccStr = `${cc}`.trim()
    if (/^[A-Za-z]{2}$/.test(ccStr)) {
      return getCountryCodeFromIso2(ccStr.toUpperCase())
    }
    return ccStr.replace(/\D/g, '')
  }

  const dialFromCustomer = resolveDialFromCustomerCountry()

  if (dialFromCustomer && digits.startsWith(dialFromCustomer)) {
    return {
      countryCode: dialFromCustomer,
      phoneNumber: digits.slice(dialFromCustomer.length),
    }
  }

  const sortedDialCodes = getSupportedCountries()
    .map((c) => c.countryCode)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)

  for (const dial of sortedDialCodes) {
    if (digits.startsWith(dial)) {
      return {
        countryCode: dial,
        phoneNumber: digits.slice(dial.length),
      }
    }
  }

  if (dialFromCustomer && digits) {
    return { countryCode: dialFromCustomer, phoneNumber: digits }
  }

  return { countryCode: '', phoneNumber: digits }
}

export {
  getIos2FromCountryCode,
  getCountryCodeFromIso2,
  getPhoneNumberConfigBeginWith,
  getPhoneNumberByIsoCode2,
  getPhoneNumberWithSpaces,
  parseCustomerPhoneForAuth,
}
